import { attributeAsInt, emptyChildren, isNullOrEmpty, query, queryDocument, queryFirstSibling, querySiblings } from "../../../utils";
import fetchBasketLimits from "./api/fetchBasketLimits";
import { ApiResponseWithData, BasketItem, EventPurchaseLimits } from "./api/models";
import { getBasketData } from "./api/getBasketData";
import { getBasketItemAttributes } from "./getBasketAttributes";
import { isBasketPage } from "../common/isBasketPage";
import fetchEventLimits from "./api/fetchEventLimits";
import { hasV2Basket } from "./hasV2Basket";

export async function updateDropdownsFromServer(parent?: HTMLElement) {
  
  const eventPageId = getCurrentEventPageId();

  const basketLimits = await getDropdownLimits();
  if (!basketLimits) {
    return;
  }

  let orderItems: BasketItem[] = [];

  if (await hasV2Basket()) {
    const response = await getBasketData();

    if (response.success && eventPageId === response.data.cmsContentId) {
      orderItems = response.data.orderItems;
    }
  }

  const dropdowns = parent ? 
    query<HTMLSelectElement>(parent, "select.qty") : 
    queryDocument<HTMLSelectElement>("select.qty");

  dropdowns.forEach((dropdown) => {
    const isOnBasketPage = isBasketPage();
    const siblingElement = isOnBasketPage
      ? queryFirstSibling(dropdown.parentElement, "[data-basket]") ?? queryFirstSibling(dropdown.parentElement, "[data-add-to-basket-v2]")
      : queryFirstSibling(dropdown.parentElement, "[data-add-to-basket-v2]") ?? queryFirstSibling(dropdown.parentElement, "[data-basket]");
    if (!siblingElement) {
      return;
    }

    const max = attributeAsInt(dropdown, "data-max");
    const [, eventId, productId, areaId] = getBasketItemAttributes(siblingElement);

    const eventItemsBasketCount = orderItems.filter((o) => o.eventId === eventId).reduce((acc, item) => acc + item.quantity, 0);

    const limitsForEvent = basketLimits.find((limit) => limit.id === eventId);
    const eventLimit = limitsForEvent?.eventLimit ?? 0;

    if (limitsForEvent && eventItemsBasketCount >= eventLimit && !isOnBasketPage) {
      disableDropdownOptions(dropdown);
      return;
    }

    const itemInBasket = orderItems?.find(
      (item) => item.eventId === eventId && item.productId === productId && item.areaId === areaId
    );

    const areaLimit = limitsForEvent.areas[areaId];
    const areaQuantity = orderItems?.reduce((acc, item) => {
      return item.eventId === eventId && item.areaId === areaId ? acc + item.quantity : acc;
    }, 0);

    if (areaQuantity >= areaLimit && !isOnBasketPage) {
      disableDropdownOptions(dropdown);
      return;
    }

    const productLimit = limitsForEvent.products[productId];
    const productQuantity = itemInBasket ? itemInBasket.quantity : 0;
    if (productQuantity >= productLimit && !isOnBasketPage) {
      disableDropdownOptions(dropdown);
      return;
    }

    const min = attributeAsInt(dropdown, "data-min");
    const increment = attributeAsInt(dropdown, "data-increment");

    const calculatedMin =
      productQuantity > 0 ? (productLimit ? Math.min(productLimit - productQuantity, min) : increment) : min;

    let calculatedMax = max;
    if (calculatedMax > eventLimit - eventItemsBasketCount) {
      calculatedMax = isOnBasketPage
        ? productQuantity + (eventLimit - eventItemsBasketCount)
        : eventLimit - eventItemsBasketCount;
    }
    if (areaLimit && calculatedMax > areaLimit - areaQuantity) {
      calculatedMax = isOnBasketPage ? productQuantity + (areaLimit - areaQuantity) : areaLimit - areaQuantity;
    }
    if (productLimit && calculatedMax > productLimit - productQuantity && !isOnBasketPage) {
      calculatedMax = productLimit - productQuantity;
    }

    if (calculatedMax < calculatedMin && !isOnBasketPage) {
      disableDropdownOptions(dropdown);
      return;
    }

    updateDropdownOptions(dropdown, increment, isOnBasketPage ? min : calculatedMin, Math.min(calculatedMax, max));
  });
}

function updateDropdownOptions(dropdown: HTMLSelectElement, increment: number, min: number, max: number, showSign = false) {
  let selectedValue = dropdown.value;
  emptyChildren(dropdown);
  dropdown.disabled = false;

  querySiblings<HTMLButtonElement>(dropdown.parentElement, ".basket-button").forEach((sibling) => {
    sibling.disabled = false;
  });

  for (var i = min; i <= max; i = i + increment) {
    dropdown.appendChild(createOptionElement(i, i.toString() === selectedValue, showSign));
  }

  // Do we have dropdown options, and do we have a valid selected value, if not set it to the first option in the list.
  if (
    dropdown.options.length > 0 &&
    (isNullOrEmpty(selectedValue) || !Array.from(dropdown.options).some((o) => o.value === selectedValue))
  ) {
    selectedValue = dropdown.options[0].value;
  }

  dropdown.value = selectedValue;
}

function disableDropdownOptions(dropdown: HTMLSelectElement) {
  if (dropdown.disabled === true) {
    return;
  }

  emptyChildren(dropdown);
  dropdown.disabled = true;
  dropdown.appendChild(createOptionElement(0, true));
  querySiblings<HTMLButtonElement>(dropdown, ".basket-button").forEach((sibling) => {
    sibling.disabled = true;
  });
}

function createOptionElement(value: number, selected = false, showSign = false) {
  const option = document.createElement("option");
  option.value = value.toString();
  option.innerText = `${showSign ? "+" : ""}${value}`;

  if (selected) {
    option.classList.add("empty");
    option.selected = true;
  }

  return option;
}

async function getDropdownLimits() {
  const fetchLimits = isBasketPage() ? fetchBasketLimits : getCurrentEventLimits;
  const { success, data: basketLimits } = await fetchLimits();
  if (!success) {
    return null;
  }
  return basketLimits;
}

async function getCurrentEventLimits(): Promise<ApiResponseWithData<EventPurchaseLimits[]>> {
  const eventPageId = getCurrentEventPageId();

  return fetchEventLimits(eventPageId);
}

function getCurrentEventPageId() {
  const eventPageIdField = <HTMLInputElement>document.getElementById("eventPageId");
  if (!eventPageIdField || isNullOrEmpty(eventPageIdField.value)) {
    return null;
  }

  return parseInt(eventPageIdField.value);
}
